import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { CalendarDays, Clock, CheckCircle, AlertCircle, ChevronRight } from 'lucide-react';
import clsx from 'clsx';

interface AvailableSlot {
  datum: string;
  tijd: string;
  resourceNaam: string;
}

interface BookingInfo {
  status: string;
  winkelNaam: string;
  type: string;
  klantNaam: string;
  adres?: string;
  beschikbareSlots: AvailableSlot[];
  bericht?: string;
}

const TYPE_LABELS: Record<string, string> = {
  OPMETING: 'Opmeting',
  DNV: 'DNV inspectie',
  NAZORG: 'Nazorg bezoek',
};

function formatDatum(dateStr: string) {
  return new Intl.DateTimeFormat('nl-BE', { weekday: 'long', day: 'numeric', month: 'long' }).format(new Date(dateStr));
}

export function SelfServiceBookingPage() {
  const { token } = useParams<{ token: string }>();
  const [geselecteerd, setGeselecteerd] = useState<AvailableSlot | null>(null);
  const [geboekt, setGeboekt] = useState(false);
  const [opmerkingen, setOpmerkingen] = useState('');
  const [stap, setStap] = useState<'datum' | 'bevestig' | 'klaar'>('datum');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['booking', token],
    queryFn: async () => {
      const res = await fetch(`/api/booking/${token}`);
      if (!res.ok) throw new Error('Niet gevonden');
      return res.json() as Promise<BookingInfo>;
    },
    enabled: !!token,
  });

  const boekMutation = useMutation({
    mutationFn: async () => {
      if (!geselecteerd) throw new Error('Geen slot geselecteerd');
      const res = await fetch(`/api/booking/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ datum: geselecteerd.datum, tijd: geselecteerd.tijd, opmerkingen }),
      });
      if (!res.ok) throw new Error('Boeking mislukt');
      return res.json();
    },
    onSuccess: () => {
      setGeboekt(true);
      setStap('klaar');
    },
  });

  if (isLoading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-brand border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (isError || !data) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg p-8 text-center max-w-md">
        <AlertCircle size={48} className="text-red-300 mx-auto mb-4" />
        <h1 className="text-xl font-bold text-gray-900 mb-2">Link niet gevonden</h1>
        <p className="text-gray-500 text-sm">Deze link is ongeldig of verlopen.</p>
      </div>
    </div>
  );

  if (data.status === 'GEBOEKT') return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg p-8 text-center max-w-md">
        <CheckCircle size={48} className="text-green-500 mx-auto mb-4" />
        <h1 className="text-xl font-bold text-gray-900 mb-2">Al ingepland</h1>
        <p className="text-gray-500 text-sm">{data.bericht}</p>
      </div>
    </div>
  );

  // Groepeer slots per datum
  const slotsByDatum = new Map<string, AvailableSlot[]>();
  for (const slot of (data.beschikbareSlots || [])) {
    if (!slotsByDatum.has(slot.datum)) slotsByDatum.set(slot.datum, []);
    slotsByDatum.get(slot.datum)!.push(slot);
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="bg-brand px-6 py-5 text-white">
          <div className="flex items-center gap-2 mb-1">
            <CalendarDays size={18} />
            <span className="text-sm font-medium opacity-90">{data.winkelNaam}</span>
          </div>
          <h1 className="text-xl font-bold">{TYPE_LABELS[data.type] || data.type} inplannen</h1>
          <p className="text-sm opacity-80 mt-1">Beste {data.klantNaam}</p>
        </div>

        <div className="p-6">
          {stap === 'klaar' ? (
            <div className="text-center py-4">
              <CheckCircle size={48} className="text-green-500 mx-auto mb-3" />
              <h2 className="text-lg font-bold text-gray-900 mb-2">Afspraak ingepland!</h2>
              {geselecteerd && (
                <p className="text-sm text-gray-600">
                  {formatDatum(geselecteerd.datum)} om {geselecteerd.tijd}
                </p>
              )}
              <p className="text-xs text-gray-400 mt-3">U ontvangt een bevestiging van de winkel.</p>
            </div>
          ) : stap === 'bevestig' && geselecteerd ? (
            <div className="space-y-4">
              <h2 className="font-semibold text-gray-900">Bevestig uw afspraak</h2>

              <div className="bg-brand/10 border border-brand/30 rounded-xl p-4 space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <CalendarDays size={16} className="text-brand" />
                  <span className="font-medium">{formatDatum(geselecteerd.datum)}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Clock size={16} className="text-brand" />
                  <span>{geselecteerd.tijd} (± 2 uur)</span>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">
                  Opmerkingen (optioneel)
                </label>
                <textarea
                  value={opmerkingen}
                  onChange={e => setOpmerkingen(e.target.value)}
                  placeholder="Bijzonderheden, toegang, parkeren..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none h-20 focus:outline-none focus:ring-2 focus:ring-brand/50"
                />
              </div>

              {boekMutation.isError && (
                <div className="text-red-600 text-sm bg-red-50 px-3 py-2 rounded-lg">
                  Er is een fout opgetreden. Probeer opnieuw.
                </div>
              )}

              <div className="flex gap-2">
                <button
                  onClick={() => setStap('datum')}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50"
                >
                  Terug
                </button>
                <button
                  onClick={() => boekMutation.mutate()}
                  disabled={boekMutation.isPending}
                  className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-brand rounded-xl hover:bg-[#b8973f] disabled:opacity-50"
                >
                  {boekMutation.isPending ? 'Bezig...' : 'Bevestigen'}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <h2 className="font-semibold text-gray-900">Kies een datum en tijdstip</h2>

              {slotsByDatum.size === 0 ? (
                <div className="text-center py-8 text-gray-500 text-sm">
                  <CalendarDays size={32} className="mx-auto mb-2 text-gray-300" />
                  <p>Geen beschikbare slots gevonden.</p>
                  <p className="text-xs text-gray-400 mt-1">Neem contact op met de winkel.</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1">
                  {Array.from(slotsByDatum.entries()).map(([datum, slots]) => (
                    <div key={datum}>
                      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 px-1">
                        {formatDatum(datum)}
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {slots.map(slot => (
                          <button
                            key={`${slot.datum}-${slot.tijd}`}
                            onClick={() => {
                              setGeselecteerd(slot);
                              setStap('bevestig');
                            }}
                            className={clsx(
                              'flex items-center justify-between px-3 py-2.5 rounded-xl border text-sm font-medium transition-all',
                              geselecteerd?.datum === slot.datum && geselecteerd?.tijd === slot.tijd
                                ? 'border-brand bg-brand/10 text-brand'
                                : 'border-gray-200 hover:border-brand/50 hover:bg-gray-50 text-gray-700'
                            )}
                          >
                            <span className="flex items-center gap-1.5">
                              <Clock size={14} />
                              {slot.tijd}
                            </span>
                            <ChevronRight size={14} className="text-gray-400" />
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-3 bg-gray-50 text-center border-t border-gray-100">
          <p className="text-[10px] text-gray-400">Kordaro Klant Portaal</p>
        </div>
      </div>
    </div>
  );
}
